import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Box, H5, P, Divider } from "orcs-design-system";
import { isEmpty, first } from "lodash";
import { useGoalsExploreContext } from "src/contexts/goalsExploreContext";
import { useGetObjectives } from "src/contexts/goalsExploreContext/hooks";
import TreeView from "src/shared/TreeView";
import useFetchRootTeams from "src/hooks/useFetchRootTeams";
import ObjectiveTreeHeader from "./components/ObjectiveTreeHeader";
import useDisplayTreeData from "./hooks/useDisplayTreeData";

const GoalsExplore = () => {
  const {
    treeData,
    loading,
    error,
    loadBranch,
    handleRootDataLoaded,
    handleAppendRootData,
    loadingNodeIds,
  } = useGoalsExploreContext();

  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const { rootTeams } = useFetchRootTeams({
    skip: !!selectedNodeId,
  });

  const {
    loading: rootLoading,
    error: rootError,
    hasMore,
    totalCount,
    totalFetchedCount,
    loadingMore,
    handleLoadMore,
  } = useGetObjectives({
    onCompleted: handleRootDataLoaded(rootTeams),
    objectiveIds: first(rootTeams)?.objectiveIds || [],
    handleAppendRootData,
    skip: !!selectedNodeId || isEmpty(rootTeams),
  });

  const { displayTreeData, onNodeSelected } = useDisplayTreeData({
    treeData,
    hasMore,
    loadingMore,
    handleLoadMore,
    totalFetchedCount,
    totalCount,
  });

  const onNodeToggled = useCallback(
    (node) => {
      setSelectedNodeId(node.id);
      if (node.toggled && !node.group) {
        loadBranch({ nodeId: node.id });
      }
    },
    [loadBranch]
  );

  const customizeTreeHeader = useCallback(
    (props) => {
      function CustomHeader({ node }) {
        return (
          <ObjectiveTreeHeader
            {...props}
            node={node}
            loadingNodeIds={loadingNodeIds}
          />
        );
      }
      CustomHeader.propTypes = {
        node: PropTypes.object.isRequired,
      };
      return CustomHeader;
    },
    [loadingNodeIds]
  );

  const isLoading = loading || rootLoading;
  const combinedError = error || rootError;

  if (isLoading && isEmpty(treeData)) {
    return (
      <Box p="m">
        <P>Loading objectives...</P>
      </Box>
    );
  }

  if (combinedError) {
    return (
      <Box p="m">
        <P color="danger">
          Error loading objectives: {combinedError?.message || combinedError}
        </P>
      </Box>
    );
  }

  if (isEmpty(treeData)) {
    return (
      <Box p="m">
        <P>No objectives found</P>
      </Box>
    );
  }

  return (
    <Box>
      <H5 fontWeight="bold" mb="r">
        Goals
      </H5>
      <Divider mb="r" light />
      <TreeView
        data={displayTreeData}
        onNodeToggled={onNodeToggled}
        onNodeSelected={onNodeSelected}
        customizeTreeHeader={customizeTreeHeader}
        loadingNodeIds={loadingNodeIds}
      />
    </Box>
  );
};

export default GoalsExplore;
